import Label from '@/components/elements/Label/Label';
import Icon from '@/components/icons/Icon';
import { IconVariant } from '@/components/icons/types';
import { ReadMore } from '@/components/modules/readmore';
import useMobileView from '@/hooks/useMobileView';
import { __, _s } from '@/locale';
import Symbol from '../Symbol';
import Card from './Card';

const baseTranslationKey = 'components.elements.Card.ServiceCard';

const MIDDLE_DOT_STYLE =
  'before:w-xxs before:h-xxs before:mr-2xs before:bg-fg_tertiary before:inline-block before:rounded-full before:content-[""]';

type PriceType = 'from' | 'variable' | 'free' | 'range' | 'standard' | 'optional';

type ServiceCardProps = {
  name: string;
  highlightedName?: React.ReactNode;
  id?: number;
  bundle?: { quantity: number; validity: string };
  description?: string;
  icon?: IconVariant;
  employeeName?: string;
  duration?: string;
  firstAvailableTime?: string;
  price?: { value: number[]; type: PriceType };
  priceLabel?: string;
  discount?: { amount?: number; percentage?: number; validity?: string };
  onShowReadMore?: (title: string, text: string, id: number) => void;
  onRowClick?: () => void;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  tags?: { wellnessCard?: boolean; campaign?: boolean; waitingList?: boolean };
  readMore?: React.ReactNode;
};

const getFormattedPrice = ({
  value,
  type,
  isMobileView,
}: {
  value: number[];
  type: PriceType;
  isMobileView: boolean;
}) => {
  if (type === 'from' || type === 'standard') {
    return value[0].toString() + ' ' + _s('kr');
  } else if (type === 'variable') {
    return _s(`${baseTranslationKey}.price.variable`);
  } else if (type === 'free') {
    return _s('freePrice');
  } else if (type === 'range') {
    return value[0] + ' - ' + value[1] + ' ' + _s('kr');
  } else if (type === 'optional') {
    return isMobileView ? _s('salonGiftCardDescription') : _s(`${baseTranslationKey}.price.optional`);
  }
};

const getPriceLabel = (type: PriceType) => {
  switch (type) {
    case 'from':
      return _s(`${baseTranslationKey}.price.label.from`);
    case 'variable':
      return _s(`${baseTranslationKey}.price.label.variable`);
    case 'range':
      return _s(`${baseTranslationKey}.price.label.range`);
    case 'standard':
      return _s(`${baseTranslationKey}.price.label.standard`);
    case 'optional':
      return _s(`${baseTranslationKey}.price.label.optional`);
    default:
      return '';
  }
};

const ServiceCard = ({
  name,
  highlightedName,
  id,
  bundle,
  icon = 'tag',
  employeeName,
  description,
  duration,
  price,
  discount,
  onShowReadMore,
  firstAvailableTime,
  onRowClick,
  rightSlot,
  leftSlot,
  tags,
  readMore,
}: ServiceCardProps) => {
  const validPriceTypeForDiscount = price?.type === 'from' || price?.type === 'standard';
  const hasLabels = tags?.campaign || tags?.wellnessCard || discount?.percentage || tags?.waitingList;
  const { isMobileView } = useMobileView();

  const finalFormattedPrice = price
    ? getFormattedPrice({ type: price.type, value: price.value, isMobileView: isMobileView })
    : undefined;

  const discountedFormattedPrice =
    discount?.amount && validPriceTypeForDiscount
      ? getFormattedPrice({ type: price.type, value: [price?.value[0] - discount.amount], isMobileView: isMobileView })
      : undefined;

  const handleShowReadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onShowReadMore(name, description, id);
  };

  return (
    <>
      <Card size="sm">
        <div
          onClick={onRowClick}
          role={onRowClick ? 'button' : ''}
          className={`gap-sm flex ${isMobileView ? 'flex-col' : ''}`}>
          <div className={`flex flex-1 items-center justify-between`}>
            <div className={`gap-sm flex flex-1 overflow-hidden ${hasLabels ? 'items-start' : 'items-center'}`}>
              {!isMobileView && !leftSlot && icon && (
                <Symbol size="md" children={<Icon variant={icon} color="fg_secondary" />} />
              )}
              {!isMobileView && leftSlot}
              <div className="gap-xs flex min-w-0 flex-1 flex-col">
                <div className="flex justify-between">
                  <div className="gap-sm flex min-w-0 flex-1">
                    {isMobileView && !leftSlot && icon && (
                      <Symbol size="md" children={<Icon variant={icon} color="fg_secondary" />} />
                    )}
                    {isMobileView && leftSlot}
                    <div
                      className={`gap-2xs flex min-w-0 flex-1 flex-col overflow-hidden ${
                        isMobileView ? 'justify-center' : ''
                      }`}>
                      <h2 className="text-l text-fg_primary">{highlightedName ?? name}</h2>
                      {employeeName && <span className="text-m text-fg_secondary">{employeeName}</span>}

                      <div className="text-s gap-2xs flex items-center">
                        {(duration || bundle || discount?.validity) && (
                          <div className={`flex items-center ${readMore ? 'w-full' : ''}`}>
                            {!isMobileView && duration && (
                              <span className="text-fg_secondary whitespace-nowrap font-bold">{duration}</span>
                            )}
                            {bundle?.quantity && !isMobileView && (
                              <span
                                className={`text-s text-fg_secondary ml-2xs my-1 font-bold ${
                                  duration ? `${MIDDLE_DOT_STYLE} flex before:mt-[5px]` : ''
                                }`}>{`${_s('buyBundles.quantity', {
                                count: bundle.quantity,
                              })}`}</span>
                            )}
                            {(bundle?.validity || discount?.validity) && (
                              <span
                                className={`text-fg_secondary text-s whitespace-nowrap ${
                                  isMobileView ? '' : 'ml-2xs my-1'
                                } ${
                                  (bundle?.quantity || duration) && !isMobileView
                                    ? `${MIDDLE_DOT_STYLE} flex before:mt-[5px]`
                                    : ''
                                }`}>
                                {bundle?.validity ?? `${__('appliedUntil')}${discount?.validity}`}
                              </span>
                            )}
                          </div>
                        )}

                        {description && !isMobileView && !readMore && (
                          <ReadMore
                            text={description}
                            maxLines={1}
                            className={`text-fg_secondary ${
                              duration ? `${MIDDLE_DOT_STYLE}` : ''
                            } text-m flex min-w-0 items-center before:shrink-0`}
                            onShowReadMore={onShowReadMore ? () => onShowReadMore(name, description, id) : undefined}
                          />
                        )}
                        {!isMobileView && readMore}
                      </div>
                      {firstAvailableTime && <span className="text-m text-fg_secondary">{firstAvailableTime}</span>}
                    </div>
                    {readMore && isMobileView && <div className="flex flex-shrink-0 items-center">{readMore}</div>}
                  </div>

                  {description && isMobileView && onShowReadMore && (
                    <div className={`text-s text-fg_link flex flex-shrink-0 ${isMobileView ? '' : 'items-end'}`}>
                      <button className="outline-none" onClick={handleShowReadMore}>
                        {isMobileView ? (
                          <Icon variant="info-circle" style={{ width: '20px', height: '20px' }} color="fg_link" />
                        ) : (
                          'Läs mer'
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {hasLabels && (
                  <div className="space-x-sm">
                    {tags.waitingList && <Label variant="fully-booked" label={__('fullBookedWaitlist')} />}
                    {tags?.campaign && !discount.percentage && (
                      <Label variant="campaign" icon={{ variant: 'campaign' }} />
                    )}
                    {tags?.wellnessCard && (
                      <Label variant="wellness" icon={{ variant: 'wellness' }} label={__('wellness')} />
                    )}
                    {discount.percentage && (
                      <Label variant="smt" label={`-${discount.percentage}%`} icon={{ variant: 'smt' }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={`border-border_regular ${isMobileView && (price || duration) ? 'border-t' : ''} ${
              !isMobileView && price ? 'border-l' : ''
            }`}>
            <div
              className={`text-s gap-sm flex h-full items-center ${
                isMobileView ? 'pt-sm justify-between' : 'pl-sm py-xs'
              }`}>
              <div className="gap-2xs flex flex-col justify-center">
                {price && (
                  <div className={`flex ${isMobileView ? 'gap-xs' : 'gap-2xs flex-col justify-center'} items-center`}>
                    {!isMobileView && price.type !== 'free' && !Boolean(discount?.amount) && (
                      <span className="text-s text-fg_secondary w-full text-right">{getPriceLabel(price.type)}</span>
                    )}
                    {Boolean(discount?.amount) && isMobileView && discountedFormattedPrice && (
                      <span className="text-m text-fg_highlight font-bold">{discountedFormattedPrice}</span>
                    )}
                    <span
                      className={`${
                        Boolean(discount?.amount) && discountedFormattedPrice
                          ? 'text-fg_tertiary line-through'
                          : ' font-bold'
                      }`}>
                      {`${price.type === 'from' && isMobileView ? 'Fr. ' : ''}${finalFormattedPrice}`}
                    </span>
                    {Boolean(discount?.amount) && !isMobileView && discountedFormattedPrice && (
                      <span className="text-m text-fg_highlight font-bold">{discountedFormattedPrice}</span>
                    )}
                  </div>
                )}
                {isMobileView && (duration || bundle?.quantity) && (
                  <div className="flex">
                    {duration && <span className="text-fg_secondary whitespace-nowrap font-bold">{duration}</span>}
                    {bundle?.quantity && (
                      <span
                        className={`text-s text-fg_secondary ml-2xs flex font-bold ${
                          duration ? `${MIDDLE_DOT_STYLE} before:mt-[5px]` : ''
                        }`}>{`${_s('buyBundles.quantity', {
                        count: bundle.quantity,
                      })}`}</span>
                    )}
                  </div>
                )}
              </div>

              {rightSlot && rightSlot}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default ServiceCard;
